import { DEFAULT_MANAGER_SETTINGS, DEFAULT_MATCHER_KEYS } from './constants'
import focusAsync from './focusAsync'
import { 
	findIndexOfItem,
	getInitialKeyActions, 
	getNextItemIndex, 
	getPrevItemIndex,
	isLetterKeyCode,
	matchesEvent
} from './utils'

export class Manager {
	constructor(settings = {}) {
		this.items = []
		this.settings = { ...DEFAULT_MANAGER_SETTINGS, ...settings }
		this.presses = getInitialKeyActions(this.settings.keys, DEFAULT_MATCHER_KEYS)
		this.search = ''
	}

	registerItem = item => {
		this.items.push(item)
	}

	unregisterItem = itemId => {
		this.items = this.items.filter(({ id }) => id !== itemId)
	}

	handleKeyDown = (e, id, key) => {
		let eventBound = false
		this.presses.forEach(({ action, matcher }) => {
			if (!matchesEvent(matcher, e)) {
				return
			}

			eventBound = true
			e.preventDefault()

			if (action === 'first') {
				this.focusFirstItem(key)
			} else if (action === 'last') {
				this.focusLastItem(key)
			} else if (action === 'next') {
				this.focusNextItem(id, key)
			} else if (action === 'prev') {
				this.focusPrevItem(id, key)
			}
		})

		if (!eventBound) {
			this.handleUnboundKey(e)
		}
	}

	handleUnboundKey = e => {
		if (!this.settings.stringSearch) {
			return
		}

		if (this.search !== '' && (e.key === ' ' || e.keyCode === 32)) {
			return -1
		}

		if (!isLetterKeyCode(e.keyCode)) {
			return -1
		}

		if (e.altKey || e.ctrlKey | e.metaKey) {
			return -1
		}

		e.preventDefault()
		this.addToSearch(String.fromCharCode(e.keyCode))
		this.runSearch()
	}

	focusFirstItem = key => {
		focusAsync(this.items[0][key])
	}

  focusItem = (itemId, key, height) => {
    const itemIndex = findIndexOfItem(this.items, itemId)
    focusAsync(this.items[itemIndex][key], height)
  }

	focusLastItem = key => {
		focusAsync(this.items[this.items.length - 1][key])
	}

	focusNextItem = (itemId, key) => {
		const itemIndex = findIndexOfItem(this.items, itemId)
		const targetIndex = getNextItemIndex(this.items.length, itemIndex, this.settings.wrap)
		focusAsync(this.items[targetIndex][key])
	}

	focusPrevItem = (itemId, key) => {
		const itemIndex = findIndexOfItem(this.items, itemId)
		const targetIndex = getPrevItemIndex(this.items.length, itemIndex, this.settings.wrap)
		focusAsync(this.items[targetIndex][key])
	}

	moveFocusByString = str => {
		for (const item of this.items) {
			if (!item.text) {
				continue
			}

			if (item.text.indexOf(str) === 0) {
				return focusAsync(item[this.settings.searchKey])
			}
		}
	}

	addToSearch = letter => {
		this.search += letter.toLowerCase()
	}

	runSearch = () => {
		this.startSearchRefreshTimer()
		this.moveFocusByString(this.search)
	}

	startSearchRefreshTimer = () => {
		clearTimeout(this.timer)
		this.timer = setTimeout(() => {
			this.search = ''
		}, this.settings.searchDelay)
	}
}

export default function createFocusManager(settings) {
	return new Manager(settings)
}
